/* eslint-disable no-undef */
import browserClient from './browser-client';
import disabled from './disabled';
import uniq from 'lodash/uniq';
import { pageLoadMetrics } from '../metrics/lib/pageLoadMetrics';
import { coreWebVitals } from '../metrics/lib/coreWebVitals';
import getUniqueTagsByKey from './utils/get-unique-tags-by-key';

export default (config) => {
  const { host, isEnabled = true, prefix, globalTags = [] } = config;

  if (isEnabled && host) {
    const client = browserClient({ host });

    const addPrefix = (path) => (prefix ? `${prefix}.${path}` : path);
    const objectToTags = (obj) =>
      Object.keys(obj).map((key) => `${key}:${obj[key]}`);

    let sentInitialLoadTime = false;

    let globalTagsCache = [...globalTags];

    return {
      count: (path, tags = [], count) =>
        client.count(addPrefix(path), [...globalTagsCache, ...tags], count),
      timing: (path, duration, tags = []) =>
        client.timing(addPrefix(path), duration, [...globalTagsCache, ...tags]),
      gauge: (path, value, tags = []) =>
        client.timing(addPrefix(path), value, [...globalTagsCache, ...tags]),
      addGlobalTags: (tags) => {
        globalTagsCache = uniq([...globalTagsCache, ...tags]);
      },
      replaceGlobalTags: (tags) => {
        globalTagsCache = getUniqueTagsByKey(globalTagsCache, tags);
      },
      sendPagePerformance: (
        tags = [],
        options = {
          lookupConnectTime: true,
          firstLastByte: true,
          domTiming: true,
          chromeMetrics: true,
          coreWebVitals: true,
        },
      ) => {
        if (!window.performance || !window.performance.timing) {
          return;
        }

        const sendMetrics = () => {
          const sendTimingMetric = (metric) =>
            client.timing(addPrefix('pageload'), metric.duration, [
              ...globalTagsCache,
              ...tags,
              `stage:${metric.stage}`,
            ]);

          const sendDistributionMetric = (metric) =>
            client.distribution(
              addPrefix('pageload-distribution'),
              metric.duration,
              [...globalTagsCache, ...tags, `stage:${metric.stage}`],
            );

          const sendWebVitalMetric = (metric) => {
            sendTimingMetric(metric);
            sendDistributionMetric(metric);
          };

          // use window.setTimeout to ensure the function is called after load finished in order to get the loadEventEnd time
          window.setTimeout(() => {
            pageLoadMetrics(window, options).forEach(sendTimingMetric);
          }, 0);

          if (options.coreWebVitals) {
            coreWebVitals(sendWebVitalMetric);
          }
        };

        // window.performance.timing.loadEventStart > 0 means the window.load event already triggered,
        // then we need to call sendPageLoadMetrics directly because the load event will not trigger again
        if (window.performance.timing.loadEventStart > 0) {
          sendMetrics();
        } else {
          window.addEventListener('load', () => sendMetrics());
        }
      },
      sendInitialLoadTime: (page) => {
        if (
          !sentInitialLoadTime &&
          window.performance &&
          window.performance.now
        ) {
          const MINUTE = 60 * 1000;
          const tags = objectToTags({ page });
          const elapsedMilliseconds = window.performance.now();

          // we have issues with browsers reporting insane load times (up to 30 years) which ruin our averages.
          if (elapsedMilliseconds <= 5 * MINUTE) {
            client.timing(addPrefix('initial-load'), elapsedMilliseconds, [
              ...globalTagsCache,
              ...tags,
            ]);
          }
        }
        sentInitialLoadTime = true;
      },
      flush: () => client.flush(),
    };
  }

  return disabled();
};



// WEBPACK FOOTER //
// ./src/providers/client.js